import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Plus, Trash2, FileText, Users, Shield, ShieldCheck, Zap, ChevronUp, ChevronDown, ClipboardCheck } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getResourceRoles, type ResourceRole } from '../../lib/resourceRoles';
import {
  listDocumentTemplates,
  listDocumentProcedures,
  saveDocumentProcedure,
  deleteDocumentProcedure,
  PROCEDURE_TRIGGER_EVENTS,
  type DocumentProcedure,
  type DocumentProcedureTarget,
  type DocumentProcedureTrigger,
  type DocumentTemplate,
  type ProcedureTargetType,
  type ProcedureTriggerEvent,
  type ProcedureKind,
  type ProcedureFacing,
  type ProcedureEnforcement,
} from '../../lib/documents';

interface ProcedureBuilderModalProps {
  businessId: string;
  /** When editing, the document whose procedure flow we're managing. */
  templateId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

interface ActiveResource { id: string; first_name: string | null; last_name: string | null }

let uidCounter = 0;
const newUid = () => `step-${Date.now()}-${uidCounter++}`;

/** A single step in the document's procedure flow → one document_procedures row. */
interface DraftStep {
  uid: string;
  procedureId?: string; // existing procedure (preserved on save)
  type: ProcedureKind; // 'completion' | 'verification' | 'booking'
  targets: DocumentProcedureTarget[];
  triggers: DocumentProcedureTrigger[]; // completion only
  facing?: ProcedureFacing; // booking only
  enforcement?: ProcedureEnforcement; // booking only
}

const makeCompletionStep = (): DraftStep => ({
  uid: newUid(),
  type: 'completion',
  targets: [{ target_type: 'everyone', target_id: null }],
  triggers: [{ event: 'clock_out', mandatory: true }],
});
const makeVerificationStep = (): DraftStep => ({
  uid: newUid(),
  type: 'verification',
  targets: [{ target_type: 'owner', target_id: null }],
  triggers: [],
});
const makeBookingStep = (): DraftStep => ({
  uid: newUid(),
  type: 'booking',
  targets: [],
  triggers: [],
  facing: 'staff',
  enforcement: 'required',
});

export default function ProcedureBuilderModal({ businessId, templateId: initialTemplateId, onClose, onSaved }: ProcedureBuilderModalProps) {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [roles, setRoles] = useState<ResourceRole[]>([]);
  const [resources, setResources] = useState<ActiveResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const [templateId, setTemplateId] = useState(initialTemplateId || '');
  const [steps, setSteps] = useState<DraftStep[]>([]);
  const [initialProcedureIds, setInitialProcedureIds] = useState<string[]>([]);

  const isEditing = !!initialTemplateId;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [tpls, rls, res, procs] = await Promise.all([
          listDocumentTemplates(businessId),
          getResourceRoles(businessId).catch(() => []),
          supabase.from('resources').select('id, first_name, last_name').eq('business_id', businessId).eq('is_active', true).order('first_name', { ascending: true }),
          initialTemplateId ? listDocumentProcedures(businessId) : Promise.resolve([] as DocumentProcedure[]),
        ]);
        if (!active) return;
        setTemplates(tpls.filter((t) => t.status === 'published'));
        setRoles(rls as ResourceRole[]);
        setResources(((res as any)?.data as ActiveResource[]) || []);

        if (initialTemplateId) {
          const forDoc = (procs as DocumentProcedure[]).filter((p) => p.template_id === initialTemplateId);
          setInitialProcedureIds(forDoc.map((p) => p.id));
          const loaded: DraftStep[] = forDoc.map((p) => ({
            uid: newUid(),
            procedureId: p.id,
            type: p.kind,
            targets: p.kind === 'booking' ? [] : (p.targets.length ? p.targets : [{ target_type: p.kind === 'verification' ? 'owner' : 'everyone', target_id: null }]),
            triggers: p.kind === 'completion' ? (p.triggers.length ? p.triggers : [{ event: 'clock_out', mandatory: true }]) : [],
            facing: p.facing ?? 'staff',
            enforcement: p.enforcement ?? 'required',
          }));
          setSteps(loaded);
        } else {
          // New flow: start empty so the user picks the first step type (avoids a
          // redundant default completion step on, e.g., a booking-only procedure).
          setSteps([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [businessId, initialTemplateId]);

  const docTitle = useMemo(() => templates.find((t) => t.id === templateId)?.title || 'Document', [templates, templateId]);

  // ── Step mutators ──
  const removeStep = (uid: string) => setSteps((p) => p.filter((s) => s.uid !== uid));
  const moveStep = (index: number, dir: -1 | 1) => setSteps((p) => {
    const next = [...p];
    const j = index + dir;
    if (j < 0 || j >= next.length) return p;
    [next[index], next[j]] = [next[j], next[index]];
    return next;
  });
  const addStep = (type: ProcedureKind) => {
    setShowAddMenu(false);
    const factory = type === 'verification' ? makeVerificationStep : type === 'booking' ? makeBookingStep : makeCompletionStep;
    setSteps((p) => [...p, factory()]);
  };

  // ── Target/trigger mutators (per step) ──
  const addTarget = (uid: string) => setSteps((p) => p.map((s) => s.uid === uid ? { ...s, targets: [...s.targets, { target_type: s.type === 'verification' ? 'owner' : 'role', target_id: null }] } : s));
  const updateTarget = (uid: string, i: number, patch: Partial<DocumentProcedureTarget>) => setSteps((p) => p.map((s) => s.uid === uid ? { ...s, targets: s.targets.map((t, idx) => idx === i ? { ...t, ...patch } : t) } : s));
  const removeTarget = (uid: string, i: number) => setSteps((p) => p.map((s) => s.uid === uid ? { ...s, targets: s.targets.filter((_, idx) => idx !== i) } : s));

  const addTrigger = (uid: string) => setSteps((p) => p.map((s) => s.uid === uid ? { ...s, triggers: [...s.triggers, { event: 'manual', mandatory: false }] } : s));
  const updateTrigger = (uid: string, i: number, patch: Partial<DocumentProcedureTrigger>) => setSteps((p) => p.map((s) => s.uid === uid ? { ...s, triggers: s.triggers.map((t, idx) => idx === i ? { ...t, ...patch } : t) } : s));
  const removeTrigger = (uid: string, i: number) => setSteps((p) => p.map((s) => s.uid === uid ? { ...s, triggers: s.triggers.filter((_, idx) => idx !== i) } : s));

  const stepLabel = (s: DraftStep): string => {
    if (s.type === 'verification') return 'Sign-off';
    if (s.type === 'booking') return s.facing === 'customer' ? 'Booking customer sign-off' : 'Booking checklist';
    const ev = s.triggers[0]?.event;
    return ev ? (PROCEDURE_TRIGGER_EVENTS.find((e) => e.id === ev)?.label || 'Completion') : 'Completion';
  };

  const handleSave = async () => {
    setError(null);
    if (!templateId) return setError('Choose a document.');
    if (steps.length === 0) return setError('Add at least one step.');
    for (const s of steps) {
      if (s.type === 'booking') continue; // booking steps have no targets/triggers
      if (s.targets.length === 0) return setError(s.type === 'verification' ? 'Each sign-off step needs at least one verifier.' : 'Each completion step needs at least one "who" rule.');
      for (const t of s.targets) {
        if (t.target_type === 'everyone' || t.target_type === 'owner') continue;
        if (!t.target_id) return setError('Pick a role or team member for every rule.');
      }
      if (s.type === 'completion' && s.triggers.length === 0) return setError('Each completion step needs at least one trigger.');
    }

    setSaving(true);
    try {
      const keptIds: string[] = [];
      for (const s of steps) {
        const id = await saveDocumentProcedure({
          id: s.procedureId,
          business_id: businessId,
          name: `${docTitle} · ${stepLabel(s)}`,
          template_id: templateId,
          kind: s.type,
          facing: s.type === 'booking' ? s.facing : null,
          enforcement: s.type === 'booking' ? s.enforcement : null,
          targets: s.type === 'booking' ? [] : s.targets,
          triggers: s.type === 'completion' ? s.triggers : [],
        });
        keptIds.push(id);
      }
      // Delete procedures removed from the flow.
      const removed = initialProcedureIds.filter((id) => !keptIds.includes(id));
      for (const id of removed) await deleteDocumentProcedure(id);

      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save procedures.');
    } finally {
      setSaving(false);
    }
  };

  const selectCls = 'text-[13px] border border-gray-300 rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30';

  const renderTargets = (s: DraftStep) => (
    <div className="space-y-2">
      {s.targets.map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          <select
            value={t.target_type}
            onChange={(e) => updateTarget(s.uid, i, { target_type: e.target.value as ProcedureTargetType, target_id: null })}
            className={selectCls}
          >
            {s.type === 'verification' ? (
              <>
                <option value="owner">Business admin (owner)</option>
                <option value="role">Role</option>
                <option value="resource">Specific team member</option>
              </>
            ) : (
              <>
                <option value="everyone">Everyone</option>
                <option value="role">Role</option>
                <option value="resource">Specific team member</option>
              </>
            )}
          </select>
          {t.target_type === 'role' && (
            <select value={t.target_id || ''} onChange={(e) => updateTarget(s.uid, i, { target_id: e.target.value })} className={`${selectCls} flex-1`}>
              <option value="">Select role…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
          {t.target_type === 'resource' && (
            <select value={t.target_id || ''} onChange={(e) => updateTarget(s.uid, i, { target_id: e.target.value })} className={`${selectCls} flex-1`}>
              <option value="">Select team member…</option>
              {resources.map((r) => <option key={r.id} value={r.id}>{`${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || 'Unnamed'}</option>)}
            </select>
          )}
          {t.target_type === 'everyone' && <div className="flex-1 text-[12px] text-gray-400 flex items-center gap-1"><Shield className="h-3.5 w-3.5" /> All active team members</div>}
          {t.target_type === 'owner' && <div className="flex-1 text-[12px] text-gray-400 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> The business owner account</div>}
          {s.targets.length > 1 && <button onClick={() => removeTarget(s.uid, i)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      <button onClick={() => addTarget(s.uid)} className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-800"><Plus className="h-3.5 w-3.5" /> Add {s.type === 'verification' ? 'verifier' : 'rule'}</button>
    </div>
  );

  const content = (
    <div className="fixed inset-0 z-[100050] bg-black/50 flex items-center justify-center p-4" onClick={saving ? undefined : onClose}>
      <div className="bg-gray-100 rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white rounded-t-2xl shrink-0">
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">{isEditing ? 'Edit document flow' : 'New document flow'}</div>
            <div className="text-[15px] font-semibold text-gray-900">Document procedures</div>
          </div>
          <button onClick={onClose} disabled={saving} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              {/* Document */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <label className="flex items-center gap-2 text-[13px] font-semibold text-gray-800 mb-1.5"><FileText className="h-4 w-4 text-blue-500" /> Document</label>
                <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={isEditing} className={`${selectCls} w-full disabled:bg-gray-50 disabled:text-gray-500`}>
                  <option value="">Choose a published document…</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
                {templates.length === 0 && <p className="text-[12px] text-amber-600 mt-1.5">No published documents yet. Publish a document first.</p>}
                <p className="text-[11px] text-gray-400 mt-1.5">Add one or more steps below — completion requirements and a verification sign-off — that apply to this document.</p>
              </div>

              {/* Steps */}
              {steps.map((s, index) => (
                <div key={s.uid} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className={`flex items-center justify-between px-4 py-2.5 border-b border-gray-100 ${s.type === 'verification' ? 'bg-indigo-50/60' : s.type === 'booking' ? 'bg-emerald-50/60' : 'bg-blue-50/40'}`}>
                    <div className="flex items-center gap-2">
                      {s.type === 'verification' ? <ShieldCheck className="h-4 w-4 text-indigo-600" /> : s.type === 'booking' ? <ClipboardCheck className="h-4 w-4 text-emerald-600" /> : <Zap className="h-4 w-4 text-blue-600" />}
                      <span className="text-[13px] font-semibold text-gray-900">{s.type === 'verification' ? 'Verification sign-off' : s.type === 'booking' ? 'Booking requirement' : 'Completion requirement'}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => moveStep(index, -1)} disabled={index === 0} className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                      <button onClick={() => moveStep(index, 1)} disabled={index === steps.length - 1} className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                      {steps.length > 1 && <button onClick={() => removeStep(s.uid)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    {s.type !== 'booking' && (
                      <div>
                        <div className="flex items-center gap-2 text-[12px] font-semibold text-gray-700 mb-1.5">
                          {s.type === 'verification' ? <><ShieldCheck className="h-3.5 w-3.5 text-gray-400" /> Who signs off</> : <><Users className="h-3.5 w-3.5 text-gray-400" /> Who it applies to</>}
                        </div>
                        {renderTargets(s)}
                      </div>
                    )}

                    {s.type === 'booking' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <div className="text-[12px] font-semibold text-gray-700 mb-1.5">Who completes it</div>
                          <select value={s.facing || 'staff'} onChange={(e) => setSteps((p) => p.map((x) => x.uid === s.uid ? { ...x, facing: e.target.value as ProcedureFacing } : x))} className={`${selectCls} w-full`}>
                            <option value="staff">Staff checklist</option>
                            <option value="customer">Customer sign-off (in person)</option>
                          </select>
                        </div>
                        <div>
                          <div className="text-[12px] font-semibold text-gray-700 mb-1.5">Enforcement</div>
                          <select value={s.enforcement || 'required'} onChange={(e) => setSteps((p) => p.map((x) => x.uid === s.uid ? { ...x, enforcement: e.target.value as ProcedureEnforcement } : x))} className={`${selectCls} w-full`}>
                            <option value="required">Required — blocks booking completion</option>
                            <option value="optional">Optional — does not block</option>
                          </select>
                        </div>
                        <p className="sm:col-span-2 text-[11px] text-gray-400">Applies to all bookings. {s.facing === 'customer' ? 'Hand the device to the customer to sign in the document.' : 'Staff complete this on the booking.'} {s.enforcement === 'required' ? 'An admin can override with a reason if it can\u2019t be completed.' : ''}</p>
                      </div>
                    )}

                    {s.type === 'completion' && (
                      <div>
                        <div className="flex items-center gap-2 text-[12px] font-semibold text-gray-700 mb-1.5"><Zap className="h-3.5 w-3.5 text-gray-400" /> When it's required</div>
                        <div className="space-y-2">
                          {s.triggers.map((t, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <select value={t.event} onChange={(e) => updateTrigger(s.uid, i, { event: e.target.value as ProcedureTriggerEvent })} className={`${selectCls} flex-1`}>
                                {PROCEDURE_TRIGGER_EVENTS.map((ev) => <option key={ev.id} value={ev.id}>{ev.label}</option>)}
                              </select>
                              <button onClick={() => updateTrigger(s.uid, i, { mandatory: !t.mandatory })} className={`text-[12px] font-medium px-2.5 py-2 rounded-lg border ${t.mandatory ? 'bg-red-50 border-red-200 text-red-600' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                                {t.mandatory ? 'Mandatory' : 'Optional'}
                              </button>
                              {s.triggers.length > 1 && <button onClick={() => removeTrigger(s.uid, i)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                            </div>
                          ))}
                          <button onClick={() => addTrigger(s.uid)} className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-800"><Plus className="h-3.5 w-3.5" /> Add trigger</button>
                        </div>
                      </div>
                    )}

                    {s.type === 'verification' && (
                      <p className="text-[11px] text-gray-400">After this document is completed it awaits this person's sign-off before it's truly complete.</p>
                    )}
                  </div>
                </div>
              ))}

              {steps.length === 0 && (
                <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-xl">
                  <div className="text-[13px] font-medium text-gray-600">No steps yet</div>
                  <div className="text-[12px] text-gray-400">Add a completion requirement, a verification sign-off, or a booking requirement.</div>
                </div>
              )}

              {/* Add step */}
              <div className="relative flex justify-center">
                <button onClick={() => setShowAddMenu((v) => !v)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-dashed border-blue-300 text-blue-600 text-[13px] font-semibold hover:bg-blue-50">
                  <Plus className="h-4 w-4" /> Add step
                </button>
                {showAddMenu && (
                  <div className="absolute top-full mt-1 z-10 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden w-64">
                    <button onClick={() => addStep('completion')} className="flex items-start gap-2 w-full text-left px-3 py-2.5 hover:bg-gray-50">
                      <Zap className="h-4 w-4 mt-0.5 text-blue-600" />
                      <span><span className="block text-[13px] font-semibold text-gray-900">Completion requirement</span><span className="block text-[11px] text-gray-500">Require at clock in/out, break, or manually.</span></span>
                    </button>
                    <button onClick={() => addStep('verification')} className="flex items-start gap-2 w-full text-left px-3 py-2.5 hover:bg-gray-50 border-t border-gray-100">
                      <ShieldCheck className="h-4 w-4 mt-0.5 text-indigo-600" />
                      <span><span className="block text-[13px] font-semibold text-gray-900">Verification sign-off</span><span className="block text-[11px] text-gray-500">A verifier signs off after completion.</span></span>
                    </button>
                    <button onClick={() => addStep('booking')} className="flex items-start gap-2 w-full text-left px-3 py-2.5 hover:bg-gray-50 border-t border-gray-100">
                      <ClipboardCheck className="h-4 w-4 mt-0.5 text-emerald-600" />
                      <span><span className="block text-[13px] font-semibold text-gray-900">Booking requirement</span><span className="block text-[11px] text-gray-500">Checklist / customer sign-off on bookings.</span></span>
                    </button>
                  </div>
                )}
              </div>

              {error && <div className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-white rounded-b-2xl flex justify-end gap-2 shrink-0">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || loading} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700 disabled:opacity-60">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : 'Save flow'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
